/**
 * Public careers API service.
 * No authentication headers required — these are public endpoints.
 */

import type { PublicCareersData, PublicJDItem } from '../types/careers';

const ATS_API_BASE = import.meta.env.VITE_ATS_API_URL || 'http://localhost:8200';
const API_BASE_URL = `${ATS_API_BASE}/api/v1`;

interface CareersApiResponse {
  success: boolean;
  data: PublicCareersData;
  message: string;
}

interface JDItemApiResponse {
  success: boolean;
  data: PublicJDItem;
  message: string;
}

export interface SubmitApplicationInput {
  applicant_name: string;
  applicant_email: string;
  applicant_phone?: string;
  cv_file?: File;
}

class CareersService {
  async getCareers(workspaceCode: string): Promise<PublicCareersData> {
    const response = await fetch(`${API_BASE_URL}/public/careers/${workspaceCode}`);
    const data: CareersApiResponse = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to load careers');
    }

    return data.data;
  }

  async getCareerJD(workspaceCode: string, jdId: string): Promise<PublicJDItem> {
    const response = await fetch(`${API_BASE_URL}/public/careers/${workspaceCode}/jds/${jdId}`);
    const data: JDItemApiResponse = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Job not found');
    }

    return data.data;
  }

  async submitApplication(
    workspaceCode: string,
    jdId: string,
    input: SubmitApplicationInput
  ): Promise<void> {
    const formData = new FormData();
    formData.append('applicant_name', input.applicant_name);
    formData.append('applicant_email', input.applicant_email);
    if (input.applicant_phone) formData.append('applicant_phone', input.applicant_phone);
    if (input.cv_file) formData.append('cv_file', input.cv_file);

    const response = await fetch(
      `${API_BASE_URL}/public/careers/${workspaceCode}/jds/${jdId}/apply`,
      {
        method: 'POST',
        body: formData,
      }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to submit application');
    }
  }
}

const careersService = new CareersService();
export default careersService;
